import { useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useTheme } from '@/context/ThemeContext';
import { useWeeklyStats } from '@/hooks/useWeeklyStats';

type ViewMode = 'haftalik' | 'gunluk';
type ChartType = 'alan' | 'bar';

const CustomTooltip = ({
  active,
  payload,
  label,
  isDark,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
  isDark: boolean;
}) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      className={`rounded-xl px-3 py-2.5 text-xs border ${
        isDark
          ? 'bg-dark-card border-dark-border text-dark-text'
          : 'bg-white border-light-border text-light-text'
      }`}
    >
      <p className={`font-semibold mb-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
        {label}
      </p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span className={isDark ? 'text-dark-muted' : 'text-light-muted'}>{p.name}:</span>
          <span className="font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function HaftalikPerformans() {
  const { isDark } = useTheme();
  const { weeklyData, dailyData, isLoading } = useWeeklyStats();
  const [viewMode, setViewMode] = useState<ViewMode>('haftalik');
  const [chartType, setChartType] = useState<ChartType>('alan');

  const data = viewMode === 'haftalik' ? weeklyData : dailyData;

  const totalKapanan = weeklyData.reduce((s, d) => s + d.kapanan, 0);
  const totalEklenen = weeklyData.reduce((s, d) => s + d.eklenen, 0);
  const totalTeklif = weeklyData.reduce((s, d) => s + d.teklif, 0);
  const totalCiro = weeklyData.reduce((s, d) => s + d.ciro, 0);

  const prevKapanan = totalKapanan > 0 ? Math.max(0, totalKapanan - weeklyData[weeklyData.length - 1]?.kapanan || 0) : 0;
  const kapananChange = totalKapanan - prevKapanan;

  const axisColor = isDark ? '#6b7280' : '#9ca3af';
  const gridColor = isDark ? '#374151' : '#f3f4f6';

  const summaryCards = [
    {
      label: 'Kapanan İlan',
      value: totalKapanan,
      change: kapananChange >= 0 ? `+${kapananChange}` : `${kapananChange}`,
      changeType: kapananChange >= 0 ? 'up' : 'down',
      icon: 'ri-checkbox-circle-line',
      color: 'text-green-500',
      bg: 'bg-green-50 dark:bg-green-900/20',
    },
    {
      label: 'Eklenen İlan',
      value: totalEklenen,
      change: totalEklenen > 0 ? `+${totalEklenen}` : '0',
      changeType: 'up',
      icon: 'ri-add-circle-line',
      color: 'text-primary-500',
      bg: 'bg-primary-50 dark:bg-primary-900/20',
    },
    {
      label: 'Teklif Aşaması',
      value: totalTeklif,
      change: totalTeklif > 0 ? `+${totalTeklif}` : '0',
      changeType: 'up',
      icon: 'ri-file-list-3-line',
      color: 'text-amber-500',
      bg: 'bg-amber-50 dark:bg-amber-900/20',
    },
    {
      label: 'Toplam Ciro',
      value: `${(totalCiro / 1000000).toFixed(1)}M ₺`,
      change: totalCiro > 0 ? '+%18' : '0',
      changeType: 'up',
      icon: 'ri-money-cny-circle-line',
      color: 'text-violet-500',
      bg: 'bg-violet-50 dark:bg-violet-900/20',
    },
  ];

  return (
    <div
      className={`rounded-xl border p-4 md:p-5 ${
        isDark ? 'bg-dark-card border-dark-border' : 'bg-white border-light-border'
      }`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-primary-500">
            <i className="ri-line-chart-line text-white text-base" />
          </div>
          <div>
            <h2 className={`text-base font-semibold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              Haftalık Performans
            </h2>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              Kapanan ilan trendi · Son 8 hafta
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div
            className={`flex items-center rounded-lg p-1 gap-1 ${
              isDark ? 'bg-dark-bg border border-dark-border' : 'bg-light-bg border border-light-border'
            }`}
          >
            {(['haftalik', 'gunluk'] as ViewMode[]).map((v) => (
              <button
                key={v}
                onClick={() => setViewMode(v)}
                className={`text-xs px-2.5 py-1 rounded-md transition-all whitespace-nowrap cursor-pointer ${
                  viewMode === v
                    ? 'bg-primary-500 text-white font-medium'
                    : isDark
                    ? 'text-dark-muted hover:text-dark-text'
                    : 'text-light-muted hover:text-light-text'
                }`}
              >
                {v === 'haftalik' ? 'Haftalık' : 'Bu Hafta'}
              </button>
            ))}
          </div>

          {/* Chart Type Toggle */}
          <div
            className={`flex items-center rounded-lg p-1 gap-1 ${
              isDark ? 'bg-dark-bg border border-dark-border' : 'bg-light-bg border border-light-border'
            }`}
          >
            <button
              onClick={() => setChartType('alan')}
              className={`w-7 h-7 flex items-center justify-center rounded-md transition-all cursor-pointer ${
                chartType === 'alan'
                  ? 'bg-primary-500 text-white'
                  : isDark
                  ? 'text-dark-muted hover:text-dark-text'
                  : 'text-light-muted hover:text-light-text'
              }`}
            >
              <i className="ri-line-chart-line text-sm" />
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`w-7 h-7 flex items-center justify-center rounded-md transition-all cursor-pointer ${
                chartType === 'bar'
                  ? 'bg-primary-500 text-white'
                  : isDark
                  ? 'text-dark-muted hover:text-dark-text'
                  : 'text-light-muted hover:text-light-text'
              }`}
            >
              <i className="ri-bar-chart-line text-sm" />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Mini Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
        {summaryCards.map((c) => (
          <div key={c.label} className={`rounded-xl px-3 py-2.5 ${c.bg}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${c.icon} text-sm ${c.color}`} />
              </div>
              <span
                className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                  c.changeType === 'up'
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}
              >
                {c.change}
              </span>
            </div>
            <p className={`text-lg font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
              {c.value}
            </p>
            <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>{c.label}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      {isLoading ? (
        <div className="h-52 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
              Veri yükleniyor...
            </span>
          </div>
        </div>
      ) : data.length === 0 ? (
        <div className="h-52 flex items-center justify-center">
          <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Henüz veri bulunmuyor
          </p>
        </div>
      ) : (
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'alan' ? (
              <AreaChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradKapanan" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradEklenen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradTeklif" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis
                  dataKey={viewMode === 'haftalik' ? 'week' : 'day'}
                  tick={{ fontSize: 11, fill: axisColor }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: axisColor }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={<CustomTooltip isDark={isDark} />}
                  cursor={{ stroke: isDark ? '#374151' : '#e5e7eb', strokeWidth: 1 }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                />
                <Area
                  type="monotone"
                  dataKey="kapanan"
                  name="Kapanan"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#gradKapanan)"
                  dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#10b981' }}
                />
                <Area
                  type="monotone"
                  dataKey="eklenen"
                  name="Eklenen"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  fill="url(#gradEklenen)"
                  dot={{ r: 3, fill: '#f59e0b', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#f59e0b' }}
                />
                <Area
                  type="monotone"
                  dataKey="teklif"
                  name="Teklif"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fill="url(#gradTeklif)"
                  dot={{ r: 3, fill: '#8b5cf6', strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: '#8b5cf6' }}
                />
              </AreaChart>
            ) : (
              <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barGap={3}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis
                  dataKey={viewMode === 'haftalik' ? 'week' : 'day'}
                  tick={{ fontSize: 11, fill: axisColor }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: axisColor }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={<CustomTooltip isDark={isDark} />}
                  cursor={{ fill: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                />
                <Bar dataKey="kapanan" name="Kapanan" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={20} />
                <Bar dataKey="eklenen" name="Eklenen" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={20} />
                <Bar dataKey="teklif" name="Teklif" fill="#8b5cf6" radius={[4, 4, 0, 0]} maxBarSize={20} />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Footer insight */}
      <div className={`mt-3 pt-3 border-t flex items-start gap-2 ${isDark ? 'border-dark-border' : 'border-light-border'}`}>
        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`ri-arrow-up-line text-sm text-green-500`} />
        </div>
        <p className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          {totalKapanan > 0 && totalEklenen > 0 ? (
            <>
              Son 8 haftada <span className="font-semibold text-green-500">{totalKapanan} kapanan ilan</span> mevcut.
              Eklenen ilan sayısı kapanan ilanın <span className="font-semibold">{Math.max(1, Math.round(totalEklenen / Math.max(1, totalKapanan)))}x</span>&apos;i — dönüşüm oranını artırmak için takip yoğunlaştırılabilir.
            </>
          ) : (
            <>Henüz yeterli veri bulunmuyor. FİZBO ilan ekledikçe performans analizi burada görünecek.</>
          )}
        </p>
      </div>
    </div>
  );
}
